/**
 * QuantumFlowSync.AI - Employee Detail Page
 * View full employee profile with roles, permissions, and activity
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  Shield,
  Activity,
  UserX,
  UserCheck,
  Trash2,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const EmployeeDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRoleDialog, setShowRoleDialog] = useState(false);

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/employees/${id}`, {
        withCredentials: true
      });
      setEmployee(response.data);
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirm('Are you sure you want to deactivate this employee?')) return;

    try {
      await axios.post(`${API_URL}/api/admin/employees/${id}/deactivate`, {}, {
        withCredentials: true
      });
      toast.success('Employee deactivated successfully');
      fetchEmployee();
    } catch (error) {
      console.error('Failed to deactivate employee:', error);
      toast.error('Failed to deactivate employee');
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/employees/${id}/activate`, {}, {
        withCredentials: true
      });
      toast.success('Employee activated successfully');
      fetchEmployee();
    } catch (error) {
      console.error('Failed to activate employee:', error);
      toast.error('Failed to activate employee');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this employee? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/employees/${id}`, {
        withCredentials: true
      });
      toast.success('Employee deleted successfully');
      navigate('/admin/employees');
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast.error('Failed to delete employee');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={variants[status] || variants.inactive}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Employee not found</p>
        <Button onClick={() => navigate('/admin/employees')} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/employees')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{employee.name}</h1>
            <p className="text-gray-600 mt-1">{employee.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/admin/employees/${id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          {employee.status === 'active' ? (
            <Button variant="outline" onClick={handleDeactivate}>
              <UserX className="mr-2 h-4 w-4" />
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" onClick={handleActivate}>
              <UserCheck className="mr-2 h-4 w-4" />
              Activate
            </Button>
          )}
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Profile Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{employee.email}</p>
              </div>
            </div>
            {employee.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{employee.phone}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium capitalize">{employee.department}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-2">Status</p>
              {getStatusBadge(employee.status)}
            </div>
            <div>
              <p className="text-sm text-gray-500">2FA Enabled</p>
              <p className="font-medium">
                {employee.two_fa_enabled ? (
                  <Badge className="bg-green-100 text-green-800">Yes</Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800">No</Badge>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Joined</p>
                <p className="font-medium">
                  {new Date(employee.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Last Login</p>
                <p className="font-medium">
                  {employee.last_login 
                    ? new Date(employee.last_login).toLocaleString()
                    : 'Never'}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Sessions</p>
              <p className="font-medium">{employee.session_count || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="activity">Activity Log</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Assigned Roles</CardTitle>
                  <CardDescription>
                    Roles determine what this employee can access and do
                  </CardDescription>
                </div>
                <Button onClick={() => setShowRoleDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Assign Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {employee.roles && employee.roles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employee.roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="font-medium">{role.display_name}</p>
                              <p className="text-sm text-gray-500">{role.description}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge>Tier {role.tier}</Badge>
                        </TableCell>
                        <TableCell>
                          {role.assigned_at 
                            ? new Date(role.assigned_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {role.expires_at 
                            ? new Date(role.expires_at).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            Revoke
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No roles assigned</p>
                  <Button onClick={() => setShowRoleDialog(true)} className="mt-4">
                    Assign First Role
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardHeader>
              <CardTitle>Effective Permissions</CardTitle>
              <CardDescription>
                All permissions this employee has through their roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {employee.permissions && employee.permissions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {employee.permissions.map((perm, index) => (
                    <Badge key={index} variant="outline" className="justify-start">
                      {perm}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No permissions</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>
                Actions performed by this employee
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-gray-500 py-8">
                Activity log coming soon
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>Active Sessions</CardTitle>
              <CardDescription>
                Devices and browsers where this employee is logged in
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-gray-500 py-8">
                Session management coming soon
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeDetailPage;
